import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/*
 * Add-to-cart for the cart-drawer upsell and for buy boxes set to "Add to cart
 * and open the cart drawer". Both replay the sequence Horizon's own product
 * form uses — dispatch a CartLinesUpdateEvent carrying a deferred promise, add
 * the lines, then resolve the promise with the re-rendered drawer sections — so
 * the drawer, the cart icon and the totals all update through the theme's own
 * listeners instead of a second, competing refresh.
 *
 * Listeners are delegated from document: the drawer is morphed after every
 * change and its buttons are replaced each time.
 */

/**
 * @param {HTMLElement} origin   element the event is dispatched from
 * @param {FormData} body        id, quantity and optional selling_plan
 * @param {string} source
 */
async function addToCart(origin, body, source) {
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean))];
  if (sectionIds.length) body.set('sections', sectionIds.join(','));

  const id = String(body.get('id'));
  const quantity = Number(body.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const cfg = fetchConfig('javascript', { body });
    const added = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    }).then((r) => r.json());

    if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');

    // Bundled sections already reflect the completed mutation. Reuse that
    // authoritative cart snapshot rather than waiting for a second request.
    let cart;
    for (const html of Object.values(added.sections || {})) {
      if (!html) continue;
      const state = html.match(/<script[^>]*data-vy-cart-state[^>]*>([\s\S]*?)<\/script>/i);
      if (!state) continue;
      try { cart = JSON.parse(state[1]); } catch { /* Fall back below. */ }
      if (Array.isArray(cart?.items) && Number.isFinite(cart?.item_count)) break;
      cart = null;
    }
    if (!cart) cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source, itemCount: quantity, sections: added.sections, didError: false },
    });
    return { ok: true };
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    return { ok: false, error: error.message || 'Please check your connection and try again.' };
  }
}

const busy = (el, on) => {
  if (on) {
    el.setAttribute('aria-busy', 'true');
    el.disabled = true;
    const label = el.querySelector('[data-buylabel]');
    if (label) { el.dataset.idleLabel = label.textContent; label.textContent = 'Adding to cart…'; }
  } else {
    el.removeAttribute('aria-busy');
    el.disabled = false;
    const label = el.querySelector('[data-buylabel]');
    if (label) {
      label.textContent = el.dataset.idleLabel || el.dataset.label || 'Add to cart';
      el.form?.querySelector('input[name="id"]:checked')?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
};

/* Upsell button inside the drawer */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-vy-upsell]') : null;
  if (!button || button.hasAttribute('aria-busy')) return;
  event.preventDefault();

  const label = button.textContent;
  busy(button, true);
  const body = new FormData();
  body.set('id', button.getAttribute('data-vy-upsell'));
  body.set('quantity', '1');

  const { ok } = await addToCart(button, body, 'vy-cart-upsell');
  if (!ok) {
    busy(button, false);
    button.textContent = label;
  }
});

/* Buy box set to add to cart: stay on the page and open the drawer */
document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement && event.target.matches('[data-vy-ajax-add]') ? event.target : null;
  if (!form) return;
  event.preventDefault();

  const cta = form.querySelector('[type="submit"]');
  if (cta?.hasAttribute('aria-busy')) return;
  if (cta) busy(cta, true);

  // FormData skips the disabled selling_plan field, so one-time options post
  // no plan at all — exactly what the regular form submit would send.
  const body = new FormData(form);
  body.delete('return_to');

  const status = form.querySelector('[data-vpp-add-status]');
  if (status) { status.textContent = ''; status.hidden = true; }
  const { ok, error } = await addToCart(form, body, 'vy-buy-box');
  if (!ok && status) { status.textContent = error; status.hidden = false; }
  if (cta) busy(cta, false);
  if (ok) {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }
});
